import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt, FaHome, FaCreditCard, FaCheck, FaTimes, FaSpinner } from 'react-icons/fa';
import { getCartItems, clearCart } from '../utils/cartUtils';
import mainService from '../services/mainService';
import { useToast } from '../contexts/ToastContext';
import config from '../services/config.json';
import './Checkout.css';

const Checkout = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [areas, setAreas] = useState([]);
  const [locations, setLocations] = useState([]);
  const [companyProfile, setCompanyProfile] = useState({});
  const [selectedPromotion, setSelectedPromotion] = useState({});
  
  // Form data
  const [formData, setFormData] = useState({
    CustomerId: '',
    MobileNo: '',
    WhatsaapNo: '',
    Name: '',
    SubTotal: 0,
    TotalDeliveryCharge: 0,
    GrandTotal: 0,
    CustomerAddressId: '',
    Address: '',
    Location: '',
    LocationDelId: '',
    Area: '',
    AreaDelId: '',
    Lane: '',
    POBox: '',
    Building: '',
    HouseNo: '',
    Type: '',
    LAT: '',
    LNG: '',
    Code: '',
    PromotionId: '',
    PromoName: '',
    PaymentType: 'cod',
    FinalPrice: 0,
    readTerms: false,
    Remarks: ''
  });

  // Pricing calculations
  const [pricing, setPricing] = useState({
    subtotal: 0,
    deliveryCharge: 0,
    discount: 0,
    total: 0
  });

  // Modals
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otp, setOtp] = useState('');

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load cart items
      const items = getCartItems();
      setCartItems(items);
      
      // Check if user is logged in (support both tokenInfo and userData from Login)
      const tokenInfo = JSON.parse(localStorage.getItem('tokenInfo') || 'null');
      const userDataRaw = localStorage.getItem('userData');
      const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
      const customer = tokenInfo || userData;
      if (customer) {
        setIsLoggedIn(true);
        const customerId = customer.CustomerId ?? customer.Id ?? customer.customerId ?? customer.id;
        const name = customer.Name ?? customer.name ?? customer.UserName ?? customer.userName ?? '';
        const mobileNo = customer.MobileNo ?? customer.Mobile ?? customer.mobileNo ?? customer.mobile ?? '';
        const whatsaapNo = customer.WhatsaapNo ?? customer.WhatsappNo ?? customer.whatsaapNo ?? customer.WhatsaapNo ?? mobileNo;
        setFormData(prev => ({
          ...prev,
          CustomerId: customerId ?? prev.CustomerId,
          Name: name,
          MobileNo: mobileNo,
          WhatsaapNo: whatsaapNo || mobileNo
        }));
        
        if (customerId) {
          try {
            const addresses = await mainService.GetCustomerAddresses(customerId);
            setCustomerAddresses(Array.isArray(addresses) ? addresses : []);
          } catch (error) {
            console.error('Error loading addresses:', error);
          }
        }
      }

      // Load areas
      try {
        const areasData = await mainService.getAreasInDropDown();
        setAreas(areasData);
      } catch (error) {
        console.error('Error loading areas:', error);
      }

      // Load company profile
      try {
        const profile = await mainService.get('GetCompanyProfile');
        setCompanyProfile(profile);
        setPricing(prev => ({
          ...prev,
          deliveryCharge: profile.DeliveryCharge ?? 30
        }));
      } catch (error) {
        console.error('Error loading company profile:', error);
      }

      // Calculate pricing
      calculatePricing(items);
      
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePricing = (items) => {
    const subtotal = items.reduce((sum, item) => sum + ((item.OfferPrice || item.Price) * item.Quantity), 0);
    const deliveryCharge = companyProfile.DeliveryCharge ?? 30;
    const discount = selectedPromotion.Id ? subtotal * (selectedPromotion.Value / 100) : 0;
    const total = subtotal + deliveryCharge - discount;

    setPricing({
      subtotal,
      deliveryCharge,
      discount,
      total
    });

    setFormData(prev => ({
      ...prev,
      SubTotal: subtotal,
      TotalDeliveryCharge: deliveryCharge,
      GrandTotal: subtotal - discount,
      FinalPrice: total
    }));
  };

  const loadLocations = async (areaId) => {
    try {
      const locationsData = await mainService.getLocationsByEid(areaId);
      setLocations(locationsData);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const handleAreaChange = (areaId) => {
    const numericAreaId = parseInt(areaId, 10);
    const selectedArea = areas.find(area => area.Id === numericAreaId);
    setFormData(prev => ({
      ...prev,
      AreaDelId: numericAreaId,
      Area: selectedArea?.Name || '',
      LocationDelId: '',
      Location: ''
    }));
    setLocations([]);
    if (numericAreaId) {
      loadLocations(numericAreaId);
    }
  };

  const handleLocationChange = (locationId) => {
    const numericLocationId = parseInt(locationId, 10);
    const selectedLocation = locations.find(location => location.Id === numericLocationId);
    setFormData(prev => ({
      ...prev,
      LocationDelId: numericLocationId,
      Location: selectedLocation?.Name || ''
    }));
    updateDeliveryCharge(numericLocationId);
  };

  const updateDeliveryCharge = async (locationId) => {
    try {
      const data = {
        LocationId: locationId,
        ClientNo: "CL712"
      };
      const charge = await mainService.getDeliveryChargeOnline(data);
      const deliveryCharge = charge > 0 ? charge : 30;
      
      setPricing(prev => ({
        ...prev,
        deliveryCharge
      }));
      
      setFormData(prev => ({
        ...prev,
        TotalDeliveryCharge: deliveryCharge
      }));
      
      calculatePricing(cartItems);
    } catch (error) {
      console.error('Error updating delivery charge:', error);
    }
  };

  const selectAddress = (address) => {
    setFormData(prev => ({
      ...prev,
      Address: address.Address,
      Location: address.Location,
      Area: address.Area,
      AreaDelId: address.AreaDelId,
      LocationDelId: address.LocationDelId,
      POBox: address.POBox,
      Type: address.Type,
      LAT: address.LAT,
      LNG: address.LNG,
      CustomerAddressId: address.Id
    }));
    setShowAddressModal(false);
  };

  const removeAddress = () => {
    setFormData(prev => ({
      ...prev,
      Location: '',
      Address: '',
      Area: '',
      LocationDelId: '',
      AreaDelId: '',
      Lane: '',
      POBox: '',
      Building: '',
      Type: '',
      LAT: '',
      LNG: '',
      HouseNo: '',
      CustomerAddressId: ''
    }));
  };

  const checkPromotion = async () => {
    if (!formData.Code) {
      showToast('Please enter a promo code', 'info');
      return;
    }

    try {
      setLoading(true);
      const response = await mainService.post('CheckPromotion', formData);
      
      if (response.Status === 'Success') {
        setSelectedPromotion(response);
        setFormData(prev => ({
          ...prev,
          PromotionId: response.Id,
          PromoName: response.Name
        }));
        calculatePricing(cartItems);
        showToast('Promotion applied successfully!', 'success');
      } else {
        showToast(response.Status || 'Invalid promo code', 'error');
      }
    } catch (error) {
      console.error('Error checking promotion:', error);
      showToast('Error applying promo code', 'error');
    } finally {
      setLoading(false);
    }
  };

  const cancelPromotion = () => {
    setSelectedPromotion({});
    setFormData(prev => ({
      ...prev,
      PromotionId: '',
      PromoName: '',
      Code: ''
    }));
    calculatePricing(cartItems);
  };

  const validateForm = () => {
    if (cartItems.length === 0) {
      showToast('No items found in cart!', 'info');
      return false;
    }

    if (!isLoggedIn && !formData.Name) {
      showToast('Please enter your name!', 'info');
      return false;
    }

    if (!isLoggedIn && !formData.MobileNo) {
      showToast('Please enter your mobile number!', 'info');
      return false;
    }

    if (!formData.WhatsaapNo || formData.WhatsaapNo.length !== 10 || !/^\d+$/.test(formData.WhatsaapNo)) {
      showToast('Please enter a valid WhatsApp number!', 'info');
      return false;
    }

    if (!formData.Area) {
      showToast('Please choose an area!', 'info');
      return false;
    }

    if (!formData.Location) {
      showToast('Please choose a location!', 'info');
      return false;
    }

    if (!formData.Address) {
      showToast('Please enter your address!', 'info');
      return false;
    }

    if (!formData.readTerms) {
      showToast('Please accept the terms and conditions!', 'info');
      return false;
    }

    return true;
  };

  const handleCheckout = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      
      const bookingData = {
        ...formData,
        BookingDetails: cartItems.map(item => ({
          Price: item.Price,
          OfferPrice: item.OfferPrice,
          Quantity: item.Quantity,
          Total: item.Quantity * item.OfferPrice,
          BookingId: item.BookingId,
          ProductId: item.Id,
          Remarks: item.Remarks,
          ColourId: item.ColourId,
          SizeId: item.SizeId,
          ZipType: item.ZipType,
          BookingCustomizations: item.SelectedCustomizations
        }))
      };

      const response = await mainService.post('SaveBooking', bookingData);
      
      if (response.Status === 'Success') {
        // Clear cart
        clearCart();
        
        // Navigate to success page
        navigate('/booking-success', {
          state: {
            refNo: response.BookingCode,
            bookId: response.BookingId
          }
        });
      } else {
        showToast('Error processing order. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error processing checkout:', error);
      showToast('Error processing order. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="loading-checkout">
            <FaSpinner className="spinner" />
            <h2>Loading checkout...</h2>
            <p>Please wait while we prepare your order.</p>
          </div>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="checkout-page">
        <div className="container">
          <div className="empty-cart">
            <div className="empty-cart-icon">
              <FaCreditCard />
            </div>
            <h2>Your cart is empty</h2>
            <p>Please add some items to your cart before proceeding to checkout.</p>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/products')}
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="container">
        {/* Page Header */}
        <div className="page-header">
          <h1 className="page-title">Checkout</h1>
          <p className="page-subtitle">Complete your order — pay when you receive</p>
        </div>

        <div className="checkout-content">
          {/* Billing Information */}
          <div className="billing-section">
            <div className="section-header">
              <h2>Billing Information</h2>
            </div>

            {!formData.CustomerAddressId ? (
              <div className="billing-form">
                {customerAddresses.length > 0 && (
                  <div className="address-selection">
                    <button 
                      className="btn btn-secondary address-btn"
                      onClick={() => setShowAddressModal(true)}
                    >
                      <FaHome />
                      Select Address from List
                    </button>
                  </div>
                )}

                <div className="">
                  <div className="form-group">
                    <label>Your Name <span>*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.Name}
                      onChange={(e) => setFormData(prev => ({ ...prev, Name: e.target.value }))}
                      disabled={isLoggedIn}
                    />
                  </div>
                </div>

                <div className="">
                  <div className="form-group">
                    <label>Mobile Number <span>*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="ex: 0501231234"
                      value={formData.MobileNo}
                      onChange={(e) => setFormData(prev => ({ ...prev, MobileNo: e.target.value }))}
                      disabled={isLoggedIn}
                    />
                  </div>
                </div>

                <div className="">
                  <div className="form-group">
                    <label>WhatsApp Number <span>*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="ex: 0501231234"
                      value={formData.WhatsaapNo}
                      onChange={(e) => setFormData(prev => ({ ...prev, WhatsaapNo: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="">
                  <div className="form-group">
                    <label>Area <span>*</span></label>
                    <select
                      className="form-control"
                      value={formData.AreaDelId}
                      onChange={(e) => handleAreaChange(e.target.value)}
                    >
                      <option value="">Select Area</option>
                      {areas.map(area => (
                        <option key={area.Id} value={area.Id}>
                          {area.Name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="">
                  <div className="form-group">
                    <label>Location <span>*</span></label>
                    <select
                      className="form-control"
                      value={formData.LocationDelId}
                      onChange={(e) => handleLocationChange(e.target.value)}
                      disabled={!formData.AreaDelId}
                    >
                      <option value="">Select Location</option>
                      {locations.map(location => (
                        <option key={location.Id} value={location.Id}>
                          {location.Name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="">
                  <div className="form-group">
                    <label>Address <span>*</span></label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.Address}
                      onChange={(e) => setFormData(prev => ({ ...prev, Address: e.target.value }))}
                      placeholder="Enter your complete address"
                    />
                  </div>
                </div>

                <div className="">
                  <div className="form-group">
                    <label>Order Notes</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.Remarks}
                      onChange={(e) => setFormData(prev => ({ ...prev, Remarks: e.target.value }))}
                      placeholder="Any special instructions for your order"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="selected-address">
                <div className="address-card">
                  <h3>Delivering to:</h3>
                  <div className="address-details">
                    <p><strong>Area:</strong> {formData.Area}</p>
                    <p><strong>Location:</strong> {formData.Location}</p>
                    <p><strong>Address:</strong> {formData.Address}</p>
                  </div>
                  <div className="address-actions">
                    <button 
                      className="btn btn-secondary"
                      onClick={() => setShowAddressModal(true)}
                    >
                      <FaHome />
                      Change Address
                    </button>
                    <button 
                      className="btn btn-outline"
                      onClick={removeAddress}
                    >
                      <FaTimes />
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="order-summary">
            <div className="section-header">
              <h2>Order Summary</h2>
            </div>

            <div className="order-items">
              {cartItems.map((item, index) => (
                <div key={`${item.Id}-${index}`} className="order-item">
                  <div className="item-image3">
                    <img src={config.apiUrl + item.Image} alt={item.Name} />
                  </div>
                  <div className="item-details">
                    <h4>{item.Name}</h4>
                    <div className="item-options">
                      {item.ColorName && <span>Color: {item.ColorName}</span>}
                      {item.SizeName && <span>Size: {item.SizeName}</span>}
                      {item.ZipType && <span>Type: {item.ZipType}</span>}
                    </div>
                    <div className="item-quantity">Qty: {item.Quantity}</div>
                  </div>
                  <div className="item-price">
                    AED {((item.OfferPrice || item.Price) * item.Quantity).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>

            <div className="pricing-breakdown">
              <div className="pricing-item">
                <span>Subtotal ({cartItems.length} items)</span>
                <span>AED {pricing.subtotal.toFixed(2)}</span>
              </div>
              
              {pricing.discount > 0 && (
                <div className="pricing-item discount">
                  <span>Discount ({selectedPromotion.Name})</span>
                  <span>-AED {pricing.discount.toFixed(2)}</span>
                </div>
              )}
              
              <div className="pricing-item">
                <span>Delivery Charges</span>
                <span>{pricing.deliveryCharge === 0 ? 'Free' : `${pricing.deliveryCharge} AED`}</span>
              </div>
              
              <div className="pricing-item total">
                <span>Total</span>
                <span>AED {pricing.total.toFixed(2)}</span>
              </div>
            </div>

            {/* Promo Code */}
            {!selectedPromotion.Id && (
              <div className="promo-section">
                <div className="promo-input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter promo code"
                    value={formData.Code}
                    onChange={(e) => setFormData(prev => ({ ...prev, Code: e.target.value }))}
                  />
                  <button 
                    className="btn btn-primary"
                    onClick={checkPromotion}
                    disabled={loading}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}

            {selectedPromotion.Id && (
              <div className="applied-promo">
                <div className="promo-info">
                  <FaCheck className="success-icon" />
                  <span>Promotion Applied: {selectedPromotion.Name}</span>
                  <span className="savings">You saved AED {pricing.discount.toFixed(2)}</span>
                </div>
                <button 
                  className="btn btn-outline"
                  onClick={cancelPromotion}
                >
                  <FaTimes />
                </button>
              </div>
            )}

            {/* Terms and Conditions */}
            <div className="terms-section">
              <div className="terms-text">
                <p>
                  Your order will be delivered within 2-5 days. Please be sure that you will receive it because we made it specially on your request. 
                  Always keep the cash on hand if you go out to guarantee the delivery on time.
                </p>
              </div>
              <div className="terms-checkbox">
                <input
                  type="checkbox"
                  id="terms"
                  checked={formData.readTerms}
                  onChange={(e) => setFormData(prev => ({ ...prev, readTerms: e.target.checked }))}
                />
                <label htmlFor="terms">I read and confirm the terms and conditions</label>
              </div>
            </div>

            {/* Place Order - Cash on Delivery only */}
            <div className="payment-actions">
              <button 
                type="button"
                className="btn checkout-btn checkout-btn-cod"
                onClick={handleCheckout}
                disabled={submitting || !formData.readTerms}
              >
                <FaMapMarkerAlt className="checkout-btn-icon" />
                <span className="checkout-btn-text">
                  {submitting ? 'Placing order...' : 'Place order — Cash on delivery'}
                </span>
                <span className="checkout-btn-amount">AED {pricing.total.toFixed(2)}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Address Selection Modal */}
      {showAddressModal && (
        <div className="modal-overlay" onClick={() => setShowAddressModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select Address</h3>
              <button 
                className="modal-close"
                onClick={() => setShowAddressModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="address-grid">
                {customerAddresses.map((address) => (
                  <div key={address.Id} className="address-card">
                    <div className="address-info">
                      <p><strong>Area:</strong> {address.Area}</p>
                      <p><strong>Location:</strong> {address.Location}</p>
                      <p><strong>Address:</strong> {address.Address}</p>
                    </div>
                    <button 
                      className="btn btn-primary"
                      onClick={() => selectAddress(address)}
                    >
                      Select
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
